import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  CreateQrCodePayload,
  QrCodeDto,
} from "@/features/merchant/api/dto";

/**
 * Génère un QR code pour un marchand
 * (`POST /QrCodes/merchant/{merchantId}/add?actingMerchantId=...`).
 * Réservé au marchand lié (self-service) ou au back-office (guide §6.6).
 */
export const useCreateQrCode = (merchantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "qr-codes", "create", merchantId],
    mutationFn: ({
      body,
      actingMerchantId,
    }: {
      body: CreateQrCodePayload;
      actingMerchantId?: string;
    }) => {
      const query = actingMerchantId
        ? `?actingMerchantId=${actingMerchantId}`
        : "";
      return mutateData({
        url: `${merchantEndpoints.qrCodes.add(merchantId)}${query}`,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<QrCodeDto>;
    },
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        [QUERY_KEYS.MERCHANT_QR_CODES_BY_MERCHANT(merchantId)],
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateQrCode failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
